import type { BookerMatchReason, BookerSuggestions } from '../../../api/comms';
import type { BookerPick } from './bookingForm';

/**
 * The words the booker picker puts on a candidate, kept out of the component so
 * they can be pinned. Every suggestion says WHY it was offered: "same domain"
 * is a reason to look and never a reason to pick, and an agent who can see that
 * is the whole case for John Smith at Acme can tell it apart from the one whose
 * name also matches.
 */
export const reasonLabel = (reason: BookerMatchReason, domain: string | null, searching = false): string => {
  switch (reason) {
    case 'name':
      // A search hit CONTAINS what was typed; a suggestion IS the same name.
      return searching ? 'name matches' : 'same name';
    case 'domain':
      return domain ? `@${domain}` : 'same domain';
    case 'linked':
      return 'linked to a colleague';
    case 'email':
      return 'email matches';
    case 'phone':
      return 'phone matches';
    default:
      return reason;
  }
};

/** The query worth sending: one character asks nothing, so it reads as "show suggestions". */
export const effectiveQuery = (typed: string): string => {
  const text = typed.trim();
  return text.length >= 2 ? text : '';
};

/**
 * The line above the list: a short label, and the WHY behind a "?" rather than
 * spelled out on every open. Free mail still gets its own explanation —
 * otherwise "nothing suggested" for a gmail sender reads as "dispatch has
 * nobody", which is the wrong conclusion.
 */
export const pickerLead = (
  result: BookerSuggestions | null,
  query: string,
): { label: string; tip: string | null } => {
  if (query) return { label: `Results for “${query}”`, tip: null };
  if (!result) return { label: 'Looking for likely accounts…', tip: null };
  if (result.freeMail) {
    return {
      label: 'Suggested',
      tip: 'Free-mail addresses like gmail.com are shared by everybody, so only the same name is suggested.',
    };
  }
  if (result.domain) {
    return {
      label: 'Suggested',
      tip:
        `The same name, colleagues at @${result.domain} someone already linked, and other ` +
        `@${result.domain} accounts. A shared domain is a hint, not a match — pick only the right person.`,
    };
  }
  return { label: 'Suggested', tip: 'Accounts with the same name as this contact.' };
};

export const pickerEmpty = (query: string): string =>
  query ? `Nobody in dispatch matches “${query}”.` : 'No likely matches.';

/**
 * The few likeliest accounts, as one-tap "Possibly:" buttons beside a Guest
 * chip. Customers first (most mail is from people, and the ranking already put
 * the strongest reason on top), then affiliates, and never more than `limit` —
 * past three it is a list, which is what the picker is for.
 */
export const topCandidates = (
  result: BookerSuggestions | null,
  limit = 3,
): { key: string; pick: BookerPick; label: string; hint: string }[] => {
  if (!result) return [];
  const customers = result.customers.map((customer) => ({
    key: `c${customer.id}`,
    pick: { type: 'customer' as const, customer },
    label: [customer.name || `Customer #${customer.id}`, customer.businessName].filter(Boolean).join(' · '),
    hint: customer.matchedOn.map((r) => reasonLabel(r, result.domain)).join(', '),
  }));
  const affiliates = result.affiliates.map((affiliate) => ({
    key: `a${affiliate.id}`,
    pick: { type: 'affiliate' as const, affiliate },
    label: `${affiliate.companyName || `Affiliate #${affiliate.id}`} · affiliate`,
    hint: affiliate.matchedOn.map((r) => reasonLabel(r, result.domain)).join(', '),
  }));
  return [...customers, ...affiliates].slice(0, limit);
};

/**
 * Which tab opens: customers, unless the suggestions found only affiliates — a
 * farm-in desk on a new address should not open on an empty list.
 */
export const defaultPickerTab = (result: BookerSuggestions | null): 'customers' | 'affiliates' =>
  result && !result.customers.length && result.affiliates.length && !result.unavailable.affiliates
    ? 'affiliates'
    : 'customers';
